/**
 * Google Analytics Utility Functions
 * Provides type-safe wrapper functions for Google Analytics tracking
 */

// Google Analytics ID
export const GA_TRACKING_ID = 'G-F376K2D9Q8';

// Extend window interface to include gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Check if Google Analytics is loaded
export const isGALoaded = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Track page views
export const trackPageView = (url: string, title?: string): void => {
  if (!isGALoaded()) {
    console.warn('Google Analytics not loaded');
    return;
  }

  try {
    window.gtag('config', GA_TRACKING_ID, {
      page_location: url,
      page_title: title,
    });
    console.log('📊 GA Page View:', { url, title });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Track custom events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
): void => {
  if (!isGALoaded()) {
    console.warn('Google Analytics not loaded');
    return;
  }

  try {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
    console.log('📊 GA Event:', { action, category, label, value });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Track user interactions
export const trackUserInteraction = (
  action: string,
  label?: string,
  value?: number
): void => {
  trackEvent(action, 'user_interaction', label, value);
};

// Track navigation events
export const trackNavigation = (destination: string, source?: string): void => {
  trackEvent('navigation', 'page_change', `${source || 'unknown'}_to_${destination}`);
};

// Track search events
export const trackSearch = (searchTerm: string, resultsCount?: number): void => {
  window.gtag('event', 'search', {
    search_term: searchTerm,
    value: resultsCount,
  });
  console.log('📊 GA Search:', { searchTerm, resultsCount });
};

// Track form submissions
export const trackFormSubmission = (formName: string, success: boolean): void => {
  trackEvent(success ? 'form_submit_success' : 'form_submit_error', 'form', formName);
};

// Track authentication events
export const trackAuthEvent = (action: 'login' | 'signup' | 'logout', method?: string): void => {
  window.gtag('event', action, {
    method: method || 'unknown',
  });
  console.log('📊 GA Auth:', { action, method });
};

// Track errors
export const trackError = (error: string, context?: string): void => {
  trackEvent('error', 'exception', `${context || 'unknown'}: ${error}`);
};

// Track file downloads
export const trackDownload = (fileName: string, fileType: string): void => {
  trackEvent('download', 'file', `${fileType}:${fileName}`);
};

// Track outbound links
export const trackOutboundLink = (url: string): void => {
  trackEvent('click', 'outbound_link', url);
};

// Track social sharing
export const trackSocialShare = (platform: string, content: string): void => {
  trackEvent('share', 'social', `${platform}:${content}`);
};

// Track video interactions
export const trackVideoEvent = (
  action: 'play' | 'pause' | 'complete',
  videoTitle: string,
  progress?: number
): void => {
  trackEvent(`video_${action}`, 'video', videoTitle, progress);
};

// Track e-commerce events (for future use)
export const trackPurchase = (transactionId: string, value: number, currency = 'INR'): void => {
  window.gtag('event', 'purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency,
  });
};

// Track custom Durga Puja specific events
export const trackDurgaPujaEvent = (
  action: 'view_event' | 'create_event' | 'register_event' | 'vote' | 'feature_event',
  eventName?: string,
  location?: string
): void => {
  trackEvent(action, 'durga_puja', eventName ? `${eventName}:${location || 'unknown'}` : location);
};

// Track calendar interactions
export const trackCalendarEvent = (action: 'view' | 'date_click' | 'export', date?: string): void => {
  trackEvent(`calendar_${action}`, 'calendar', date);
};

// Track dashboard interactions
export const trackDashboardEvent = (action: string, section?: string): void => {
  trackEvent(`dashboard_${action}`, 'dashboard', section);
};

// Initialize Google Analytics with enhanced configuration
export const initializeGA = (): void => {
  if (!isGALoaded()) {
    console.warn('Google Analytics not available');
    return;
  }

  // Enhanced ecommerce settings (for future use)
  window.gtag('config', GA_TRACKING_ID, {
    // Enhanced measurement
    enhanced_measurement: true,
    // Custom parameters
    custom_map: {
      dimension1: 'user_role',
      dimension2: 'page_category',
    },
    // Performance settings
    send_page_view: true,
    anonymize_ip: true,
  });

  console.log('📊 Google Analytics initialized with ID:', GA_TRACKING_ID);
};

// Set user properties
export const setUserProperties = (properties: Record<string, any>): void => {
  if (!isGALoaded()) return;

  window.gtag('config', GA_TRACKING_ID, {
    user_properties: properties,
  });
};

// Set user role for tracking
export const setUserRole = (role: string): void => {
  setUserProperties({ user_role: role });
};

// Consent management (GDPR compliance)
export const updateGAConsent = (granted: boolean): void => {
  if (!isGALoaded()) return;

  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: granted ? 'granted' : 'denied',
  });
};

export default {
  trackPageView,
  trackEvent,
  trackUserInteraction,
  trackNavigation,
  trackSearch,
  trackFormSubmission,
  trackAuthEvent,
  trackError,
  trackDownload,
  trackOutboundLink,
  trackSocialShare,
  trackVideoEvent,
  trackPurchase,
  trackDurgaPujaEvent,
  trackCalendarEvent,
  trackDashboardEvent,
  initializeGA,
  setUserRole,
  updateGAConsent,
};
